/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

import Analytics from '../analytics/Analytics';
import PersistenceController from '../persistence/PersistenceController';
import Calculator from './Calculator';
import { CalculatorResult, ParameterModifier, ParameterName, ParameterValueElement } from './calculatorTypes';
import { getDefaultParameters, isAdvanced } from './configParser';

class CalculationController {
  private static _instance: CalculationController;
  public static get instance() {
    return this._instance || (this._instance = new this());
  }

  private _currentResult: CalculatorResult | undefined = undefined;

  private get currentResult() {
    return this._currentResult;
  }
  private set currentResult(value) {
    this._currentResult = value;
    PersistenceController.store('calculatorResult', this.currentResult);
  }

  public get defaultSelectedHumidityParameter(): ParameterName {
    return 'relativeHumidity';
  }

  public begin() {
    this._currentResult = PersistenceController.retrieve('calculatorResult', CalculatorResult);
    return this.currentResult;
  }

  public reset(log: boolean = true) {
    if (log) {
      Analytics.log('calculation.reset');
    }
    this.currentResult = getDefaultParameters();
    return this.currentResult;
  }

  public resetAdvanced(elevationMode: boolean) {
    Analytics.log('calculation.reset_advanced');

    if (!this.currentResult) {
      this.reset();
    }

    const defaults = getDefaultParameters();
    const params = this.currentResult!.parameters;
    Object.keys(params).forEach((key) => {
      const parameter = key as ParameterName;
      if (isAdvanced(parameter)) {
        params[parameter] = defaults.parameters[parameter] as any;
      }
      params[parameter].tolerance = 0;
    });

    const last = this._selectedHumidityParameter;
    return this.calculate(last, 'value', this.currentResult!.parameters[last].value, elevationMode, last);
  }

  private _selectedHumidityParameter: ParameterName = this.defaultSelectedHumidityParameter;

  public trimValue = (val: number) => {
    const precision = 6;
    return Number(Math.round((val + 'e' + precision) as any) + 'e-' + precision);
  };

  private trimValues(r: CalculatorResult, except: ParameterName[]) {
    const params = r.parameters;
    for (const [key, val] of Object.entries(params)) {
      const k = key as ParameterName;
      if (!except.includes(k)) {
        params[k].value = this.trimValue(val.value);
      }
    }
  }

  public calculate(
    parameterName: ParameterName,
    element: ParameterValueElement,
    value: ParameterModifier,
    elevationMode: boolean,
    selectedHumidityParameter?: ParameterName,
  ) {
    this._selectedHumidityParameter = selectedHumidityParameter ?? this.defaultSelectedHumidityParameter;

    const newResult = Calculator.calculate(this.currentResult, this._selectedHumidityParameter, parameterName, element, value, elevationMode = false);
    if (newResult) {
      this.trimValues(newResult, ['gasType', 'psychrometer']);
      this.currentResult = newResult;
      const analyticsLabel = `${parameterName}_${element}`;
      Analytics.log('calculation.parameter', analyticsLabel, this.currentResult.parameters[parameterName][element]);
      Analytics.log('calculation.validity', this.currentResult.validityStates?.[0]?.severity, this.currentResult.validityStates?.[0]?.reason);
    } else {
      Analytics.error('error.calculation', `Failed to calculate. parameterName:${parameterName}, element:${element}, value:${value}`);
    }

    return this.currentResult;
  }

  public isHumidityParameter = (parameterName: ParameterName) =>
    !['temperature', 'pressure', 'elevation', 'gasType', 'psychrometer'].includes(parameterName);
  public isConditionsParameter = (parameterName: ParameterName) => ['temperature', 'pressure', 'elevation'].includes(parameterName);
}

export default CalculationController.instance;
